import {
  BookOpenIcon,
  CalendarIcon,
  ChatAlt2Icon,
  ChatIcon,
  GiftIcon,
  GlobeAltIcon,
  InboxIcon,
  KeyIcon,
  LockClosedIcon,
  MicrophoneIcon,
  PhotographIcon,
  TerminalIcon,
  UserIcon,
} from '@heroicons/react/outline';
import useLocale from 'next-translate/useTranslation';

import ImageQuestion from '../mocks/ImageQuestion';
import TelegramChat, { ErrorCodePhoto } from '../mocks/TelegramChat';
import TelegramSetup from '../mocks/TelegramSetup';
import type { ChannelContent, DemoBrand } from '../types';
import { useChannelsT } from '../useChannelsT';

const BRAND: DemoBrand = {
  name: 'Voltline Support',
  initial: 'V',
  bg: 'bg-[#0f766e]',
};

/*
 * Facts this page relies on (checked 2026-10-21): Telegram is in
 * FREE_PLAN_CHANNELS; the bots server answers private chats only (a group
 * gets "I can only work in private chats"), reads photos and image files
 * (up to 4, 5 MB each) when image_input_enabled is on and the model supports
 * image input, has no voice handler, refuses other files, streams replies
 * and understands /start, /clear and /ping (web-app integration/telegram.ts).
 */
export function useTelegramContent(): ChannelContent {
  const { t } = useChannelsT();
  const { t: tc } = useLocale('chatbots');

  return {
    key: 'telegram',
    name: 'Telegram',
    logo: 'FaTelegram',
    seoTitle: t(
      'Telegram AI Chatbot: Create a Telegram Bot That Answers for You',
    ),
    seoDescription: t(
      'Create an AI Telegram bot that answers customers 24/7 from your website and documents, and reads the photos they send. Works on the Free plan. No code needed.',
    ),
    hero: {
      label: t('Telegram AI chatbot'),
      title: t('A Telegram bot that knows your business.'),
      accent: t('Live in minutes.'),
      lead: t(
        'Create a bot with Telegram’s BotFather, paste its token, and your AI agent answers everyone who messages it, from your website and documents. It can even read the photos customers send.',
      ),
      facts: [
        t('Works on the Free plan'),
        t('No code, just a bot token'),
        t('Reads photos and screenshots'),
      ],
      description: t(
        'Example: a customer sends an appliance store’s Telegram bot a photo of a washing machine showing error E21. The agent reads the photo, finds the fix in the service manual and explains it.',
      ),
      demo: (
        <TelegramChat
          brand={BRAND}
          lines={[
            {
              from: 'customer',
              photo: <ErrorCodePhoto code='E21' />,
              text: t('My washing machine shows this. What should I do?'),
              time: '18:02',
              at: 600,
            },
            {
              from: 'agent',
              text: t(
                'That’s error E21: the water isn’t draining. Turn the machine off, clean the drain filter behind the small flap at the bottom front, then run a rinse.',
              ),
              time: '18:02',
              typingAt: 1200,
              at: 2300,
            },
            {
              from: 'customer',
              text: t('It works again! Thank you 🙌'),
              time: '18:09',
              at: 3400,
            },
            {
              from: 'agent',
              text: t(
                'Great news! Cleaning that filter every few months keeps E21 away.',
              ),
              time: '18:09',
              typingAt: 3800,
              at: 4600,
            },
          ]}
        />
      ),
      steps: [
        {
          icon: PhotographIcon,
          title: t('Read the photo'),
          via: t('Image attachments'),
          at: 1400,
        },
        {
          icon: BookOpenIcon,
          title: t('Found E21 in the service manual'),
          via: tc('Knowledge'),
          at: 1900,
        },
      ],
    },
    jobs: {
      title: t('More than a FAQ bot.'),
      tail: t('A helper customers actually use.'),
      lead: t(
        'People message your bot at any hour. Your agent answers from what you’ve taught it, keeps the conversation going and can act on it.',
      ),
      items: [
        {
          icon: ChatAlt2Icon,
          title: t('Answers from your knowledge'),
          detail: t(
            'Trained on your website, documents and FAQs, so every answer is about your business.',
          ),
        },
        {
          icon: PhotographIcon,
          title: t('Reads photos'),
          detail: t(
            'Customers send a photo or a screenshot and the agent works out what they mean.',
          ),
        },
        {
          icon: ChatIcon,
          title: t('Remembers the chat'),
          detail: t(
            'Each person’s conversation keeps its history, so follow-up questions just work.',
          ),
        },
        {
          icon: CalendarIcon,
          title: t('Books and collects'),
          detail: t(
            'Takes bookings in your Google Calendar and collects contact details, on paid plans.',
          ),
        },
        {
          icon: GlobeAltIcon,
          title: t('Speaks their language'),
          detail: t(
            'Replies in the language each customer writes in, from the same knowledge.',
          ),
        },
        {
          icon: TerminalIcon,
          title: t('Knows the commands'),
          detail: t(
            '/start greets people, /clear starts a fresh conversation and /ping checks the bot is up.',
          ),
        },
      ],
    },
    setup: {
      title: t('Set up in three steps.'),
      tail: t('No code, no server.'),
      steps: [
        {
          title: t('Create a bot'),
          detail: t(
            'In Telegram, message @BotFather, send /newbot and choose a name and a username that ends in “bot”.',
          ),
        },
        {
          title: t('Paste the token'),
          detail: t(
            'Copy the token BotFather sends you, paste it into your agent’s Telegram window and click Save.',
          ),
        },
        {
          title: t('Say hello'),
          detail: t(
            'Find your bot in Telegram, send /start and ask a question. Then share its t.me link anywhere.',
          ),
        },
      ],
      guide: 'https://easy-peasy.ai/docs/agents/telegram',
      guideLabel: t('Read the Telegram setup guide'),
      description: t(
        'A chat with Telegram’s BotFather creating the Voltline Support bot and sending its token, next to the agent’s Telegram window with the token pasted and the bot connected.',
      ),
      visual: <TelegramSetup botName={BRAND.name} />,
    },
    spotlight: {
      title: t('Customers can show you the problem.'),
      tail: t('Your agent reads the picture.'),
      lead: t(
        'Turn on image attachments and customers can send photos and screenshots on Telegram. With a model that can see images, the agent reads them and answers from your knowledge.',
      ),
      points: [
        t('Up to 4 photos in one message'),
        t('Photos, screenshots and images sent as files'),
        t('Works with models that can see images'),
        t('One switch in Appearance turns it on or off'),
      ],
      description: t(
        'The image attachments switch turned on, and a customer’s photo of an appliance’s rating plate, which the agent reads to name the right filter.',
      ),
      visual: (
        <ImageQuestion
          brand={BRAND}
          question={t('Which filter fits my washer?')}
          answer={t(
            'That’s a VX-8200. It takes our F-12 filter, which costs $14.',
          )}
        />
      ),
    },
    rules: {
      title: t('Good to know.'),
      tail: t('Telegram, in plain words.'),
      items: [
        {
          icon: GiftIcon,
          title: t('Free plan included'),
          detail: t(
            'Telegram works on every plan, including Free. Actions, such as bookings, need a paid plan.',
          ),
        },
        {
          icon: UserIcon,
          title: t('Private chats'),
          detail: t(
            'Your bot answers people who message it directly. It doesn’t answer in group chats.',
          ),
        },
        {
          icon: MicrophoneIcon,
          title: t('Photos, not voice'),
          detail: t(
            'Photos and screenshots work when image attachments are on. Voice messages aren’t answered, and other files can’t be opened.',
          ),
        },
        {
          icon: KeyIcon,
          title: t('One bot per agent'),
          detail: t(
            'Each bot token connects to one agent. For a second agent, create a second bot with BotFather.',
          ),
        },
        {
          icon: LockClosedIcon,
          title: t('Keep the token private'),
          detail: t(
            'Anyone with the token controls your bot. If it leaks, send /revoke to BotFather and connect the new token.',
          ),
        },
        {
          icon: InboxIcon,
          title: t('Every chat in one inbox'),
          detail: t(
            'Telegram chats show up in Conversations, next to your website, WhatsApp, Messenger and LINE chats.',
          ),
        },
      ],
    },
    faqs: [
      {
        question: t('Is it free to put an AI agent on Telegram?'),
        answer: t(
          'Yes. Telegram works on every plan, including Free. Actions such as bookings and collecting details are on paid plans.',
        ),
      },
      {
        question: t('Do I need to know how to code?'),
        answer: t(
          'No. You create a bot with Telegram’s @BotFather, paste its token into your agent and click Save. There’s no server or webhook to set up.',
        ),
      },
      {
        question: t('Can the bot answer in Telegram groups?'),
        answer: t(
          'No. It answers people who message the bot in a private chat. In a group, it replies that it only works in private chats.',
        ),
      },
      {
        question: t('Can customers send photos?'),
        answer: t(
          'Yes, when image attachments are turned on in Appearance and your agent’s model can see images. It reads up to 4 images in one message.',
        ),
      },
      {
        question: t('Does it understand voice messages?'),
        answer: t(
          'No. On Telegram it answers text and photos. Voice messages aren’t answered.',
        ),
      },
      {
        question: t('Which commands does the bot understand?'),
        answer: t(
          '/start greets people, /clear deletes their conversation history and /ping checks that the bot is running.',
        ),
      },
      {
        question: t('Can I change the bot’s name and picture?'),
        answer: t(
          'Yes, in @BotFather: /setuserpic sets its photo and /setdescription sets the text people see before they tap Start.',
        ),
      },
      {
        question: t('Which languages does it speak?'),
        answer: t(
          'It replies in the language each customer writes in, more than 80 of them, from the same knowledge.',
        ),
      },
    ],
    finalTitle: t('Your Telegram bot could be answering by lunchtime.'),
    finalExchange: {
      question: t('Do you repair dishwashers too?'),
      answer: t('Yes, every brand. Want me to find you a slot?'),
    },
  };
}
